import { useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, CheckCircle, XCircle, Clock } from 'lucide-react'

import { apiClient } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'

interface Trip {
  id: string
  trip_date: string
  trip_time: string | null
  passenger_name: string | null
  pickup_address: string
  dropoff_address: string
  distance_km: number | null
  duration_minutes: number | null
  amount: number
  cost_center: string | null
  reference: string | null
}

interface InvoiceDetail {
  id: string
  invoice_number: string
  invoice_date: string
  due_date: string | null
  period_start: string | null
  period_end: string | null
  total_amount: number
  net_amount: number | null
  vat_amount: number | null
  trip_count: number
  status: string
  trips: Trip[]
}

export default function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation('invoices')
  const { t: tc } = useTranslation('common')

  const { data: invoice, isLoading, error } = useQuery<InvoiceDetail>({
    queryKey: ['invoice', id],
    queryFn: () => apiClient.get(`/invoices/${id}`),
    enabled: !!id,
  })

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (error || !invoice) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{tc('errors.generic')}</p>
        <Link to="/invoices" className="mt-4 inline-flex items-center text-primary-600 hover:text-primary-700">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {tc('common.back')}
        </Link>
      </div>
    )
  }

  const statusIcon = {
    pending: <Clock className="w-5 h-5 text-yellow-500" />,
    reviewed: <CheckCircle className="w-5 h-5 text-blue-500" />,
    approved: <CheckCircle className="w-5 h-5 text-green-500" />,
    disputed: <XCircle className="w-5 h-5 text-red-500" />,
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link
            to="/invoices"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-2"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {tc('common.back')}
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('detail.title')} #{invoice.invoice_number}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {statusIcon[invoice.status as keyof typeof statusIcon]}
          <span className="font-medium">{t(`status.${invoice.status}`)}</span>
        </div>
      </div>

      {/* Invoice Info */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {t('detail.invoiceInfo')}
        </h2>
        <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <dt className="text-sm text-gray-500">{t('list.invoiceDate')}</dt>
            <dd className="font-medium">{new Date(invoice.invoice_date).toLocaleDateString()}</dd>
          </div>
          {invoice.due_date && (
            <div>
              <dt className="text-sm text-gray-500">{t('list.dueDate')}</dt>
              <dd className="font-medium">{new Date(invoice.due_date).toLocaleDateString()}</dd>
            </div>
          )}
          {invoice.period_start && invoice.period_end && (
            <div>
              <dt className="text-sm text-gray-500">{t('detail.period')}</dt>
              <dd className="font-medium">
                {new Date(invoice.period_start).toLocaleDateString()} - {new Date(invoice.period_end).toLocaleDateString()}
              </dd>
            </div>
          )}
          <div>
            <dt className="text-sm text-gray-500">{t('list.totalAmount')}</dt>
            <dd className="font-medium text-lg">€{Number(invoice.total_amount).toFixed(2)}</dd>
          </div>
          <div>
            <dt className="text-sm text-gray-500">{t('list.tripCount')}</dt>
            <dd className="font-medium">{invoice.trip_count}</dd>
          </div>
        </dl>
      </div>

      {/* Trips Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">{t('detail.trips')}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('trip.date')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('trip.passenger')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('trip.pickup')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('trip.dropoff')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  {t('trip.amount')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {invoice.trips.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    {t('detail.noTrips')}
                  </td>
                </tr>
              ) : (
                invoice.trips.map((trip) => (
                  <tr key={trip.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {new Date(trip.trip_date).toLocaleDateString()}
                      {trip.trip_time && ` ${trip.trip_time}`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {trip.passenger_name || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm max-w-xs truncate">
                      {trip.pickup_address}
                    </td>
                    <td className="px-6 py-4 text-sm max-w-xs truncate">
                      {trip.dropoff_address}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium">
                      €{Number(trip.amount).toFixed(2)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
